import React, {
	useState,
	useEffect,
	useContext,
	useCallback,
	useMemo
} from "react";
import "./Despatch.scss";
import useFetchData from "../Shared/FetchData/FetchData";
import { 
	getCurrentDateTruncated,
	getUpdatedDate
} from "../../utils/date.js";
import {
	StartDatePicker,
	EndDatePicker
} from "../Shared/DatePicker";
import TimePicker from "../Shared/TimePicker/TimePicker";
import DropDown from "../Shared/DropDown/DropDown";
import {
	Grid,
	Paper,
	Table,
	TableContainer,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	Box,
	Snackbar,
	MenuItem,
	Button,
	Pagination,
	TableSortLabel,
	IconButton
} from "@mui/material";
import { DataContext } from "../../App";
import { LoadingStatusTable } from "../Shared/LoadingStatus/LoadingStatus";
import { LoadableContent } from "../Shared/LoadableContent/LoadableContent";
import { AlertBox } from "../Shared/Alert/Alert";
import { DownloadCSV } from "../Shared/DownloadCSV/DownloadCSV.jsx";
import { RecordsTotal } from "../Shared/ResultsFound/ResultsFound.jsx";
import { StatusBox } from "../Shared/StatusBox/StatusBox";
import { getDisplayDateTime } from "../../utils/date.js";
import { getFormattedCutoffTime } from "../../utils/time.js";
import { PageHeading } from "../Shared/PageHeading/PageHeading";
import * as tableConstants from "../Shared/Config/TableConfig";
import PageRowsDropDown from "../Shared/PageRows/PageRows";
import { Clear } from "@mui/icons-material";

export default function Despatch() {

	// The default start date is 8 days ago.
	const getDefaultStartDate = () => {
		const date = getCurrentDateTruncated();
		date.setDate(date.getDate() - 8);
		return date;
	};

	// The default end date is yesterday.
	const getDefaultEndDate = () => {
		const date = getCurrentDateTruncated();
		date.setDate(date.getDate() - 1);
		return date;
	};

	const getDefaultTime = () => {
		return "12:00";
	};

	const context = useContext(DataContext);
	const pageRowOptions = tableConstants.PAGE_ROW_OPTIONS;

	const [state, setState] = useState({
		isLoaded: false,
		currentPage: 1,
		rowsPerPage: pageRowOptions[0],
		despatchData: {},
		despatchTableData: {},
		startDate: getDefaultStartDate(),
		endDate: getDefaultEndDate(),
		clientMintsoftId: 0,
		warehouseId: 0,
		time: getDefaultTime(),
		warehouses: [],
		disableTicketCreation: false,
		showAlert: false,
		alertSeverity: "error",
		alertMessage: "",
		sortBy: {
			fieldName: "",
			direction: "asc",
		}
	});

	const clearSortBy = useCallback(() => {
		setState(previousState => {
			return {
				...previousState,
				sortBy: {
					fieldName: "",
					direction: "asc",
				},
				isLoaded: false,
				currentPage: 1,
			};
		});
	}, [state.sortBy.fieldName, state.sortBy.direction]);

	const isSortEnabled = (fieldName) => {
		return state.sortBy.fieldName === fieldName;
	};

	const onClickSortBy = (fieldName) => {
		var direction = state.sortBy.direction === "asc" ? "desc" : "asc";
		if (!isSortEnabled(fieldName)) {
			direction = "asc";
		}
		
		setState(previousState => {
			return {
				...previousState,
				sortBy: {
					fieldName: fieldName,
					direction: direction,
				},
				isLoaded: false,
				currentPage: 1,
			};
		});
	};

	const isBezosOrderStatusSortEnabled = useMemo(() =>
		isSortEnabled("bezos_order_status"),
	[state.sortBy.fieldName]);

	const onClickSortByBezosOrderStatus = useCallback(() => 
		onClickSortBy("bezos_order_status"),
	[state.sortBy.fieldName, state.sortBy.direction]);

	const isWarehouseOrderStatusSortEnabled = useMemo(() =>
		isSortEnabled("warehouse_order_status"),
	[state.sortBy.fieldName]);

	const onClickSortByWarehouseOrderStatus = useCallback(() => 
		onClickSortBy("warehouse_order_status"),
	[state.sortBy.fieldName, state.sortBy.direction]);

	const isBezosOrderDateSortEnabled = useMemo(() =>
		isSortEnabled("bezos_order_date"),
	[state.sortBy.fieldName]);

	const onClickSortByBezosOrderDate = useCallback(() =>
		onClickSortBy("bezos_order_date"),
	[state.sortBy.fieldName, state.sortBy.direction]);

	const isClientNameSortEnabled = useMemo(() =>
		isSortEnabled("client_name"),
	[state.sortBy.fieldName]);

	const onClickSortByClientName = useCallback(() =>
		onClickSortBy("client_name"),
	[state.sortBy.fieldName, state.sortBy.direction]);

	const filterParams = useMemo(() => new URLSearchParams({
		startTime: getUpdatedDate(state.startDate, state.time, "despatch"),
		endTime: getUpdatedDate(state.endDate, state.time, "despatch"),
	}), [state.startDate, state.endDate, state.time]);

	const dataParams = useMemo(() => new URLSearchParams({
		startTime: getUpdatedDate(state.startDate, state.time, "despatch"),
		endTime: getUpdatedDate(state.endDate, state.time, "despatch"),
		clientMintsoftId: state.clientMintsoftId,
		warehouseId: state.warehouseId,
		page: state.currentPage,
		pageSize: state.rowsPerPage,
		sortByFieldName: state.sortBy.fieldName,
		sortByDirection: state.sortBy.direction,
	}), [state.startDate, 
		state.endDate,
		state.time,
		state.clientMintsoftId,
		state.warehouseId,
		state.currentPage,
		state.rowsPerPage,
		state.sortBy.fieldName,
		state.sortBy.direction,
	]);

	const onStartDateChange = (newValue) => {
		setState(previousState => {
			return {
				...previousState,
				startDate: newValue,
				isLoaded: false,
				currentPage: 1,
			};
		});
	};

	const onEndDateChange = (newValue) => {
		setState((previousState) => {
			return {
				...previousState,
				endDate: newValue,
				isLoaded: false,
				currentPage: 1,
			};
		});
	};

	const onTimeChange = (newValue) => {
		setState(previousState => {
			return {
				...previousState,
				time: newValue,
				isLoaded: false,
				currentPage: 1,
			};
		});
	};

	const onChangePageRows = (pageRows) => {
		setState(previousState => {
			return {
				...previousState,
				rowsPerPage: pageRows,
				currentPage: 1,
			};
		});
	};

	const despatchFilterOptions = useFetchData(useCallback(() =>
		context.dataProvider.getDespatchFilterOptions(filterParams),
	[context.dataProvider, filterParams]));

	const despatchDetails = useFetchData(useCallback(() =>
		context.dataProvider.getDespatchDetails(dataParams),
	[context.dataProvider, dataParams]));

	const despatchTotal = useFetchData(useCallback(() =>
		context.dataProvider.getDespatchTotal(dataParams),
	[context.dataProvider, dataParams]));

	const warehouses = useFetchData(useCallback(() =>
		context.dataProvider.getWarehouses(filterParams),
	[context.dataProvider, filterParams]));
	
	const page_data = {
		filterOptions: {
			resultsData: despatchFilterOptions.results,
			status: despatchFilterOptions.status,
			isLoading: despatchFilterOptions.isLoading,
		},
		despatch: {
			resultsData: despatchDetails.results,
			status: despatchDetails.status,
			isLoading: despatchDetails.isLoading,
		},
		despatchTotal: {
			resultsData: despatchTotal.results,
			status: despatchTotal.status,
			isLoading: despatchTotal.isLoading,
		},
		warehouse: {
			resultsData: warehouses.results,
			status: warehouses.status,
			isLoading: warehouses.isLoading,
		},
	};

	useEffect(() => {
		if (page_data.despatch.resultsData && state.warehouses) {
			const mergedData = page_data.despatch.resultsData.map(item => {
				return {
					...item,
					warehouse_name: state.warehouses[item.warehouse_id]?.name,
					bezos_order_date: getDisplayDateTime(item.bezos_order_date),
					warehouse_order_date: getDisplayDateTime(item.warehouse_order_date),
					warehouse_cutoff_time: getFormattedCutoffTime(state.warehouses[item.warehouse_id]?.cutoff_time_hour, state.warehouses[item.warehouse_id]?.cutoff_time_minute),
				};
			});
			setState(previousState => {
				return {
					...previousState,
					despatchTableData: mergedData,
				};
			});
			
		}
	}, [page_data.despatch.resultsData, state.warehouses]);

	useEffect(() => {
		const getWarehouseData = () => {
			setState(previousState => {
				return {
					...previousState,
					warehouses: page_data.warehouse.resultsData.reduce((acc, w) => {
						acc[w.id] = w;
						return acc;
					}, {}),
				};
			});
		};
		getWarehouseData();
	}, [page_data.warehouse.resultsData]);

	const getPageValue = (e) => {
		let paginationNextPage;

		if (e.currentTarget.textContent.length > 0) {
			paginationNextPage = Number(e.currentTarget.innerText);
		} else if (e.currentTarget.querySelector("svg").getAttribute("data-testid") === "NavigateNextIcon") {
			paginationNextPage = Number(state.currentPage) + 1;
		} else if (e.currentTarget.querySelector("svg").getAttribute("data-testid") === "NavigateBeforeIcon") {
			paginationNextPage = Number(state.currentPage) - 1;
		} else {
			paginationNextPage = Number(state.currentPage);
		}

		return paginationNextPage;
	};

	let paginationView;
	if (despatchTotal.isLoading || (despatchTotal.results <= state.rowsPerPage)) {
		paginationView = null;
	} else {
		const numberOfPages = Math.ceil(despatchTotal.results / state.rowsPerPage);

		paginationView = (
			<Pagination
				count={numberOfPages}
				page={state.currentPage}
				color="primary"
				siblingCount={0}
				boundaryCount={1}
				onChange={(input) => {
					setState(previousState => {
						return {
							...previousState,
							currentPage: getPageValue(input, state.currentPage)
						};
					});
				}}
			/>
		);
	}


	const TableFooter = () => {
		return <Grid container item className="table-footer" mt={3} xs={3} sm={4} lg={10} xl={12}>
			<Grid item xs />

			<Grid item mb={2} mr={2}>
				{paginationView}
			</Grid>

			<Grid item mb={2}>
				<PageRowsDropDown
					resultsTotalCount={despatchTotal.results}
					rowsPerPage={state.rowsPerPage}
					pageRowOptions={pageRowOptions}
					onChangePageRow={onChangePageRows}
				/>
			</Grid>

			<Grid item xs />
		</Grid>;
	};

	const SortableHeaderCell = ({ label, active, onClick}) =>
		<TableCell className="table-header">{label}
			<TableSortLabel
				active={active}
				onClick={onClick}
				direction={state.sortBy.direction}
			/>
			{active && (
				<IconButton
					aria-label="clear sort"
					onClick={clearSortBy}
					size="small"
					sx={{ ml: 1 }}
				>
					<Clear fontSize="small" />
				</IconButton>
			)}
		</TableCell>;

	const GetDespatchCheckerTable = () => {
		const TableData = () => {
			if (page_data.despatch.resultsData.length === 0) {
				return (
					<TableRow>
						<TableCell colSpan="9" className='table-error-message-text'>No results found</TableCell>
					</TableRow>
				);
			}

			return (
				state.despatchTableData.map((item, i) =>
					<TableRow key={i}>
						<TableCell>{item.bezos_order_number}</TableCell>
						<TableCell>{item.bezos_order_status}</TableCell>
						<TableCell>{item.warehouse_name}</TableCell>
						<TableCell>{item.client_name}</TableCell>
						<TableCell>{item.warehouse_order_number}</TableCell>
						<TableCell>
							<StatusBox 
								status={item.warehouse_order_status} 
								statusType={"order"}
							/>
						</TableCell>
						<TableCell>{item.bezos_order_date}</TableCell>
						<TableCell>{item.warehouse_order_date}</TableCell>
						<TableCell>{item.warehouse_cutoff_time}</TableCell>
					</TableRow>
				)
			);
		};

		return (
			<Paper sx={{ p: 2, mt: 4, width: "100%" }} elevation={2}>
				<Typography variant="h6" mb={2}>
                    Non-Despatched Orders <RecordsTotal total={despatchTotal.results}/>
				</Typography>
				<TableContainer sx={{ maxHeight: 600, overflow: "auto", mb:3 }}>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell className="table-header">Bezos Order Number</TableCell>
								<SortableHeaderCell
									label="Bezos Order Status"
									active={isBezosOrderStatusSortEnabled}
									onClick={onClickSortByBezosOrderStatus}
								/>
								<TableCell className="table-header">Warehouse</TableCell>
								<SortableHeaderCell
									label="Client"
									active={isClientNameSortEnabled}
									onClick={onClickSortByClientName}
								/>
								<TableCell className="table-header">Warehouse Order Number</TableCell>
								<SortableHeaderCell
									label="Warehouse Order Status"
									active={isWarehouseOrderStatusSortEnabled}
									onClick={onClickSortByWarehouseOrderStatus}
								/>
								<SortableHeaderCell
									label="Bezos Order Date"
									active={isBezosOrderDateSortEnabled}
									onClick={onClickSortByBezosOrderDate}
								/>
								<TableCell className="table-header">Warehouse Order Date</TableCell>
								<TableCell className="table-header">Warehouse Cutoff Time</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{page_data.despatch.isLoading ?
								<LoadingStatusTable message={"Loading..."} colSpan={9} />
								:
								<TableData />
							}
						</TableBody>
						<TableFooter/>
					</Table>

				</TableContainer>
				{state.despatchTableData.length > 0 &&
					<DownloadCSV 
						data={state.despatchTableData}
						csvFilename={"despatch_checker_table"}
						isLoaded={state.isLoaded}
					/>
				}
			</Paper>
		);
	};

	const onAlertHandleClose = (reason) => {
		if (reason === "clickaway") {
			return;
		}

		setState(previousState => {
			return {
				...previousState,
				showAlert: false,
			};
		});
	};

	const HeadingContent = () => (
		<Grid container mt={2}>
			<Typography variant="p">
				Use the despatch checker to view (and fix) orders that have not been despatched by the expected warehouse cut-off time.
			</Typography>
		</Grid>
	);

	const onWarehouseSelect = (newWarehouse) => {
		if (newWarehouse === 0) {
			setState(previousState => {
				return {
					...previousState,
					warehouseId: 0,
					isLoaded: false,
				};
			});
			return;
		}

		const selectedWarehouse = page_data.filterOptions.resultsData.warehouses.find(warehouse => warehouse.id === newWarehouse);
		const cutoffTimeHour = selectedWarehouse.cutoff_time_hour || 12;
		const cutoffTimeMinute = selectedWarehouse.cutoff_time_minute || 0;
		const formattedHour = cutoffTimeHour.toString().padStart(2, "0");
		const formattedMinute = cutoffTimeMinute.toString().padStart(2, "0");
		
		const cutoffTime = `${formattedHour}:${formattedMinute}`;
		
		setState(previousState => {
			return {
				...previousState,
				warehouseId: newWarehouse,
				time: cutoffTime,
				isLoaded: false,
			};
		});
	};

	const WarehouseFilter = () => {
		const warehouseOptions = () => {
			const options = [
				<MenuItem key={0} value={0}>
					All Warehouses
				</MenuItem>
			];
			if (!page_data.filterOptions.isLoading && page_data.filterOptions.resultsData.warehouses) {
				const additionalOptions = page_data.filterOptions.resultsData.warehouses.map(warehouse =>
					<MenuItem key={warehouse.id} value={warehouse.id}>
						{warehouse.short_code}
					</MenuItem> 
				);
				options.push(...additionalOptions);
			}
			return options;
		};
		return <LoadableContent isLoading={page_data.filterOptions.isLoading}>
			<DropDown
				label={"Warehouse:"}
				id={"select_warehouse"}
				onChangeSelect={onWarehouseSelect}
				selected={state.warehouseId}
				sx={{ minWidth: 140, mr: 2, mb: 2, backgroundColor: theme => theme.palette.statusbox.main }}
				size="small"
				map={warehouseOptions()}
			/>
		</LoadableContent>;
	};

	const onSellerSelect = (newSeller) => {
		setState(previousState => {
			return {
				...previousState,
				clientMintsoftId: newSeller,
				isLoaded: false,
			};
		});
	};

	const SellerFilter = () => {
		const sellerOptions = () => {
			const options = [
				<MenuItem key={0} value={0}>
					All Sellers
				</MenuItem>
			];
			if (!page_data.filterOptions.isLoading && page_data.filterOptions.resultsData.sellers) {
				const additionalOptions = page_data.filterOptions.resultsData.sellers.map(seller =>
					<MenuItem key={seller.mintsoft_id} value={seller.mintsoft_id}>
						{seller.short_name}
					</MenuItem> 
				);
				options.push(...additionalOptions);
			}
			return options;
		};
		return <LoadableContent isLoading={page_data.filterOptions.isLoading}>
			<DropDown
				label={"Seller:"}
				id={"select_seller"}
				onChangeSelect={onSellerSelect}
				selected={state.clientMintsoftId}
				sx={{ minWidth: 140, mr: 2, mb: 2, backgroundColor: theme => theme.palette.statusbox.main }}
				size="small"
				map={sellerOptions()}
			/>
		</LoadableContent>;
	};

	const onClickLastDays = (days) => {
		const startDate = getCurrentDateTruncated();
		startDate.setDate(startDate.getDate() - days);
		const endDate = getCurrentDateTruncated();
		endDate.setDate(endDate.getDate() - 1);
		setState(previousState => {
			return {
				...previousState,
				startDate: startDate,
				endDate: endDate,
				isLoaded: false,
			};
		});
	};

	const onClickLastThreeDays = () => {
		onClickLastDays(3);
	};

	const onClickLastSevenDays = () => {
		onClickLastDays(7);
	};

	const onClickLastFourteenDays = () => {
		onClickLastDays(14);
	};

	const DateTimeButton = ({ label, onClick}) => (
		<Button
			variant="contained"
			color="primary"
			onClick={onClick}
			sx={{ ml: 2, mb: 2 }}
		>
			{label}
		</Button>
	);

	const onClickResetFilters = () => {
		setState(previousState => {
			return {
				...previousState,
				startDate: getDefaultStartDate(),
				endDate: getDefaultEndDate(),
				time: getDefaultTime(),
				warehouseId: 0,
				clientMintsoftId: 0,
				isLoaded: false,
			};
		});
	};

	const ResetFiltersButton = () => (
		<Button
			variant="outlined"
			color="secondary"
			onClick={onClickResetFilters}
			sx={{ ml: 2, mb: 2 }}
		>
			Reset Filters
		</Button>
	);

	const Filters = () => (
		<Paper sx={{ p: 2, mt: 4, width: "100%" }} elevation={2}>

			<Typography variant="h6" mb={2}>
				Filters
			</Typography>

			<WarehouseFilter />

			<SellerFilter />

			<StartDatePicker 
				onChange={onStartDateChange}
				date={state.startDate}
			/>
			<EndDatePicker 
				onChange={onEndDateChange} 
				date={state.endDate}
				sx={{ mr: 2 }}
			/>
			<TimePicker
				onTimeChange={onTimeChange}
				time={state.time}
			/>

			<DateTimeButton
				label={"Last 3 days"}
				onClick={onClickLastThreeDays}
			/>
			<DateTimeButton
				label={"Last 7 days"}
				onClick={onClickLastSevenDays}
			/>
			<DateTimeButton
				label={"Last 14 days"}
				onClick={onClickLastFourteenDays}
			/>

			<ResetFiltersButton />
		</Paper>
	);


	return (
		<Box id="despatch">
			<PageHeading pageTitle={"Despatch Checker"}>
			</PageHeading>
			<HeadingContent />

			<Grid container>

				<Grid item xs={12} lg={12}>
					<Filters />
				</Grid>

				<Grid item xs={12} lg={12}>
				</Grid>

				<Grid item xs={12} lg={12}>
					<GetDespatchCheckerTable />
				</Grid>

			</Grid>

			<Snackbar open={state.showAlert} autoHideDuration={8000} onClose={onAlertHandleClose} mb={2}>
				<Box>
					<AlertBox
						onClose={onAlertHandleClose}
						severity={state.alertSeverity}
						message={state.alertMessage}
					/>
				</Box>
			</Snackbar>

		</Box>
	);
}
